import * as Sentry from '@sentry/react';

export function initSentry() {
  // Only initialize in production
  if (import.meta.env.DEV) return;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || '',
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    // Only send errors, not performance data (to stay within free tier)
    tracesSampleRate: 0,
    // Don't send PII
    sendDefaultPii: false,
    integrations: [
      // Forward every console.error to Sentry. Most catch-blocks in the app
      // already log via console.error, so this surfaces otherwise-silent bugs.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      // Capture failed network requests (e.g. failing Supabase/API calls).
      Sentry.httpClientIntegration(),
      // Attach extra (non-standard) properties of thrown error objects.
      Sentry.extraErrorDataIntegration({ depth: 5 }),
    ],
    // Drop known browser/extension noise so the dashboard stays useful.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network blips that are not actionable application bugs
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
      'TypeError: Cancelled',
      'AbortError',
      // Browser extension / third-party script noise
      'Non-Error promise rejection captured',
      /extensions\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    beforeSend(event) {
      const value = event.exception?.values?.[0]?.value || '';
      // Extra safety net for ResizeObserver noise.
      if (value.includes('ResizeObserver')) {
        return null;
      }
      return event;
    },
  });
}

export function setSentryUser(user: { id: string; email: string; role: string } | null) {
  if (import.meta.env.DEV) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
    Sentry.setTag('user_role', user.role);
  } else {
    Sentry.setUser(null);
  }
}

export function captureError(error: Error, context?: Record<string, unknown>) {
  // In production, console.error is already forwarded to Sentry by the
  // captureConsoleIntegration, so only log locally to avoid duplicate events.
  if (import.meta.env.DEV) {
    console.error(error);
    return;
  }
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context);
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

export { Sentry };
